import React, { Component } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { PrismBanner, PrismButton, PrismDialog } from "@prism-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faUndoAlt, faClock, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import PermissionLevel from "../../data/PermissionLevel";
import Loader from "../Loader";
import PageHeader from "../PageHeader";
import UserAvatar from "../UserAvatar/UserAvatar";
import { stripNameTitle } from "../../utils/nameUtils";
import "./Pledge.css";

class Pledge extends Component {
  state = {
    applications: null,
    applicationLoading: true,
    selectedFile: null,
    commitments: [
      "I am responsible for my own success in the program.",
      "I will be open to feedback, new ideas, and new experiences, and I commit to treating others with respect.",
      "I understand the expectations set for me, my required deliverables, and the commitments I am making to the relationships I will build this year.",
      "I understand that a prototype or working proof of concept is required to graduate from the program.",
      "I understand that I am expected to arrive on time or early to every session, and that disciplined use of electronics is required during sessions.",
      "I recognize that mentors, committee members, and others are investing their time and energy in my success, and I will honor that by being considerate and respectful when requesting their time.",
      "I will be responsible for meeting all program deadlines, and I understand that communications will be delivered through three channels: (1) in person at each session, (2) via email, and (3) via the Slack channel.",
      "I understand that attendance is required at all monthly sessions, and that missing more than one session may make me ineligible to graduate unless I participate in the exception process with the Committee.",
      "I will proactively communicate with my Executive Sponsor throughout the project and ensure their feedback and expectations are reflected in my final deliverable.",
      "I understand that my participation in CLEAR is a privilege, and I will represent the program and Comcast with professionalism in all interactions — inside and outside of sessions.",
    ],
    checkedItems: new Set(),
    approvedMentees: [],
    approvedAll: false,
    signedAt: null,
    allPledges: [],
    myPledges: [],
    showWithdrawModal: false,
    withdrawSelectedIds: new Set(),
    selectedAppIds: new Set(),
    userTimelines: {},     // { '${appId}-${userId}': [] | 'loading' | 'error' }
    expandedTileKey: null, // '${appId}-${userId}'
  };

  componentDidMount() {
    this._socket = io({ path: '/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });
    this._socket.on('gradPledgeUpdate', () => {
      const appIds = (this.state.applications || []).map(a => a.id);
      if (appIds.length > 0) this.checkAcknowledged(appIds);
    });

    let applicationIds = [];
    let triadApps = [];
    let permissionLevel = this.props.currentUser.Role.permission_level;

    // For Mentors, Committee, Officers, Admins,
    // Get any applications from their assigned triad.
    if (permissionLevel >= PermissionLevel.Mentor) {
      axios
        .get("api/applications/triad")
        .then((response) => {
          const currentYear = new Date().getFullYear();
          triadApps = response.data
            .map((x) => x.Application)
            .filter((a) => {
              const isMenteeApplication = a.ApplicantType.applicant_type === "mentee";
              const isForCurrentYear = a.year === currentYear;
              return isMenteeApplication && isForCurrentYear;
            });
          this.setState({ applications: triadApps });

          if (response.data) {
            response.data.forEach((r) => {
              if (r.Application && r.Application.ApplicantType.applicant_type === "mentee" && r.Application.year === currentYear) {
                applicationIds.push(r.Application.id);
              }
            });
          }
        })
        .then(() => {
          this.checkApplications(applicationIds, triadApps);
        })
        .catch((error) => {
          this.setState({ applicationLoading: false });
        });
    } else {
      this.checkApplications(applicationIds);
    }
  }

  componentWillUnmount() {
    if (this._socket) {
      this._socket.off('gradPledgeUpdate');
      this._socket.disconnect();
    }
  }

  // For mentees, managers, get all submitted applications. Triad apps are passed in
  // rather than read from this.state: React 18 batches setState inside promises, so
  // state set in the previous .then is not visible here yet.
  checkApplications(applicationIds, triadApps = []) {
    let apps = [];
    axios
      .get("api/applications/my_application")
      .then((response) => {
        apps = response.data.filter((a) => {
          let isMenteeApplication = a.ApplicantType.applicant_type === "mentee";
          let isCurrentYear = a.year === new Date().getFullYear();
          let isAccepted = a.status_id === 4;
          return isMenteeApplication && isCurrentYear && isAccepted;
        });

        apps = apps.concat(triadApps);

        applicationIds = applicationIds.concat(response.data.map((x) => x.id));
        this.setState({
          applications: apps,
          applicationLoading: applicationIds.length > 0,
        });
      })
      .then(() => {
        if (applicationIds.length > 0) {
          this.checkAcknowledged(applicationIds, apps);
        }
      })
      .catch((error) => {
        this.setState({ applicationLoading: false });
      });
  }

  // Check the current acknowledgement status of the given applications
  checkAcknowledged(appIds, knownApps) {
    axios
      .post("api/grad_pledges", {
        application_ids: appIds,
        signed_by: this.props.currentUser.user_id,
      })
      .then((resp) => {
        const approvedMentees = resp.data.map((x) => x.application_id);
        const signedAt = resp.data.length > 0 ? resp.data[0].created_at : null;
        const myPledges = resp.data;
        const allApps = knownApps || this.state.applications || [];
        const defaultSelected = new Set(allApps.filter(a => !approvedMentees.includes(a.id)).map(a => a.id));
        this.setState({ approvedMentees, signedAt, myPledges, selectedAppIds: defaultSelected });

        if (
          approvedMentees.length > 0 &&
          allApps.every((x) => approvedMentees.includes(x.id))
        ) {
          this.setState({
            approvedAll: true,
            applicationLoading: false,
          });
        } else {
          this.setState({ applicationLoading: false });
        }

        // Fetch full history for all applications (all signers)
        this.fetchHistory(appIds);
      })
      .catch((error) => {
        if (error?.response?.status !== 404) {
          console.error('Failed to check acknowledgment status:', error);
        }
        const allApps = knownApps || this.state.applications || [];
        this.setState({
          applicationLoading: false,
          selectedAppIds: new Set(allApps.map(a => a.id)),
        });
        // Still fetch full triad history even when current user has no pledges (e.g. withdrew)
        this.fetchHistory(appIds);
      });
  }

  determineUserRole(app) {
    let isMenteeApplication = app.ApplicantType.applicant_type === "mentee";
    let isApplicant = app.user_id === this.props.currentUser.user_id;
    let isManager =
      app.manager &&
      app.manager.toLowerCase() ===
        this.props.currentUser.User.email.toLowerCase();

    if (isMenteeApplication && isApplicant) {
      return "mentee";
    } else if (isManager) {
      return "manager";
    } else if (app.assignedMentor?.id === this.props.currentUser.user_id) {
      return "mentor";
    } else {
      // Default to committee for anyone else in the triad (officers, users, etc.)
      return "committee";
    }
  }

  fetchUserTimeline(appId, userId) {
    const key = `${appId}-${userId}`;
    if (this.state.userTimelines[key] && this.state.userTimelines[key] !== 'error') return;
    this.setState((prev) => ({ userTimelines: { ...prev.userTimelines, [key]: 'loading' } }));
    axios
      .get('api/grad_pledges/user-timeline', { params: { application_id: appId, user_id: userId } })
      .then((resp) =>
        this.setState((prev) => ({ userTimelines: { ...prev.userTimelines, [key]: resp.data } }))
      )
      .catch(() =>
        this.setState((prev) => ({ userTimelines: { ...prev.userTimelines, [key]: 'error' } }))
      );
  }

  toggleTileTimeline(appId, userId) {
    const key = `${appId}-${userId}`;
    this.setState((prev) => ({
      expandedTileKey: prev.expandedTileKey === key ? null : key,
    }));
    this.fetchUserTimeline(appId, userId);
  }

  fetchHistory(appIds) {
    if (!appIds || appIds.length === 0) return;
    // Invalidate timeline cache so expanded panels re-fetch fresh data after pledge/withdraw
    this.setState({ userTimelines: {}, expandedTileKey: null });
    axios
      .get('api/grad_pledges/history', {
        params: { application_ids: appIds.join(',') },
      })
      .then((resp) => this.setState({ allPledges: resp.data }))
      .catch(() => {}); // Non-critical; silently ignore
  }

  toggleCommitment(index) {
    this.setState(prev => {
      const next = new Set(prev.checkedItems);
      if (next.has(index)) {
        // Uncheck this and all subsequent
        for (let i = index; i < prev.commitments.length; i++) next.delete(i);
      } else {
        next.add(index);
      }
      return { checkedItems: next };
    });
  }

  handleSubmit(e) {
    const { applications, selectedAppIds, approvedMentees, myPledges } = this.state;
    const isMentee = this.props.currentUser.Role.name === 'mentee';

    // Belt-and-suspenders: never submit if no mentees are selected
    if (!isMentee && selectedAppIds.size === 0) return;

    const payloads = [];
    applications.forEach((a) => {
      if (selectedAppIds.has(a.id) && !approvedMentees.includes(a.id)) {
        payloads.push({
          application_id: a.id,
          signed_by: this.props.currentUser.user_id,
          signer_role: this.determineUserRole(a),
        });
      }
    });

    if (payloads.length === 0) return;

    axios.post("api/grad_pledges/ack", payloads).then((resp) => {
      const newlySignedIds = resp.data.map(x => x.application_id);
      const signedAt = resp.data.length > 0 ? resp.data[0].created_at : new Date().toISOString();
      const newMyPledges = [...myPledges, ...resp.data];
      const updatedApprovedMentees = [...approvedMentees, ...newlySignedIds];
      const allApps = applications || [];
      const approvedAll = allApps.every(a => updatedApprovedMentees.includes(a.id));
      const remainingUnsigned = new Set(
        allApps.filter(a => !updatedApprovedMentees.includes(a.id)).map(a => a.id)
      );
      this.setState({
        approvedAll,
        signedAt,
        myPledges: newMyPledges,
        approvedMentees: updatedApprovedMentees,
        selectedAppIds: remainingUnsigned,
      });
      this.fetchHistory(allApps.map(a => a.id));
    }).catch((error) => {
      console.error('Failed to acknowledge grad pledges:', error);
      alert('Failed to submit acknowledgments. Please try again.');
    });
  }

  handleRevoke() {
    // Pre-select all currently-signed mentees for withdrawal
    const signedMenteeIds = new Set(
      (this.state.applications || []).filter(a => this.state.approvedMentees.includes(a.id)).map(a => a.id)
    );
    this.setState({ showWithdrawModal: true, withdrawSelectedIds: signedMenteeIds });
  }

  confirmRevoke() {
    this.setState({ showWithdrawModal: false });
    const { myPledges, withdrawSelectedIds, applications, approvedMentees } = this.state;
    const pledgesToRevoke = myPledges.filter(p => withdrawSelectedIds.has(p.application_id));
    const idsToRevoke = pledgesToRevoke.map(p => p.id);
    const revokedAppIds = new Set(pledgesToRevoke.map(p => p.application_id));
    if (idsToRevoke.length === 0) return;

    Promise.all(idsToRevoke.map((id) => axios.delete(`api/grad_pledges/my/${id}`)))
      .then(() => {
        const appIds = (applications || []).map(a => a.id);
        const remainingMyPledges = myPledges.filter(p => !revokedAppIds.has(p.application_id));
        const remainingApprovedMentees = approvedMentees.filter(id => !revokedAppIds.has(id));
        const allApps = applications || [];
        const approvedAll = allApps.length > 0 && allApps.every(a => remainingApprovedMentees.includes(a.id));
        this.setState({
          approvedAll,
          approvedMentees: remainingApprovedMentees,
          myPledges: remainingMyPledges,
          signedAt: remainingMyPledges.length > 0 ? this.state.signedAt : null,
          checkedItems: new Set(),
          selectedAppIds: new Set([...this.state.selectedAppIds, ...revokedAppIds]),
        });
        this.fetchHistory(appIds);
      })
      .catch(() => alert('Failed to withdraw pledge. Please try again.'));
  }

  render = () => {
    if (this.state.applicationLoading) {
      return <Loader />;
    }

    const applications = this.state.applications;
    const noApplications = !applications || applications.length === 0;
    const { commitments, checkedItems, approvedAll, approvedMentees, signedAt, allPledges, showWithdrawModal, withdrawSelectedIds, selectedAppIds } = this.state;
    const allChecked = checkedItems.size === commitments.length;
    const isMentee = this.props.currentUser.Role.name === 'mentee';
    const menteeApps = applications || [];
    const canSubmit = allChecked && (isMentee || selectedAppIds.size > 0);
    const submitLabel = isMentee
      ? 'Submit Pledge'
      : selectedAppIds.size === 0
        ? 'No Mentees Selected'
        : selectedAppIds.size === 1
          ? 'Submit for 1 Mentee'
          : `Submit for ${selectedAppIds.size} Mentees`;

    const signedDate = signedAt
      ? new Date(signedAt).toLocaleString(undefined, {
          dateStyle: 'long', timeStyle: 'short',
        })
      : null;

    const formatRole = (role) =>
      (role || 'Unknown')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());

    const ROLE_ORDER = { mentee: 0, mentor: 1, committee: 2, manager: 3 };

    // Group pledges by application, sorted by role within each group
    const pledgesByApp = {};
    allPledges.forEach(p => {
      if (!pledgesByApp[p.application_id]) pledgesByApp[p.application_id] = [];
      pledgesByApp[p.application_id].push(p);
    });
    Object.values(pledgesByApp).forEach(group =>
      group.sort((a, b) => (ROLE_ORDER[a.signer_role] ?? 99) - (ROLE_ORDER[b.signer_role] ?? 99))
    );
    // historyGroupOrder: for apps with no pledges at all, still include them
    // so the pending slot renders for unsigned users with no other history yet
    const allAppIds = menteeApps.map(a => a.id);
    const historyGroupOrder = [
      ...allAppIds.filter(id => pledgesByApp[id] || !approvedMentees.includes(id)),
      ...Object.keys(pledgesByApp).map(Number).filter(id => !menteeApps.find(a => a.id === id)),
    ];

    const { userTimelines, expandedTileKey } = this.state;

    const renderTimeline = (key) => {
      const data = userTimelines[key];
      if (!data || data === 'loading') {
        return <div className="pledge-timeline-loading">Loading history…</div>;
      }
      if (data === 'error') {
        return <div className="pledge-timeline-empty">Could not load history.</div>;
      }
      if (data.length === 0) {
        return <div className="pledge-timeline-empty">No recorded history yet.</div>;
      }
      return (
        <ol className="pledge-timeline">
          {[...data].reverse().map((event, i) => (
            <li key={i} className={`pledge-timeline-event pledge-timeline-event--${event.type}`}>
              <span className="pledge-timeline-dot" />
              <div className="pledge-timeline-body">
                <span className="pledge-timeline-label">
                  {event.type === 'pledged' ? 'Pledged support' : 'Withdrew support'}
                </span>
                <span className="pledge-timeline-ts">
                  {new Date(event.timestamp).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}
                </span>
              </div>
            </li>
          ))}
        </ol>
      );
    };

    const ALL_EXPECTED_ROLES = ['mentee', 'mentor', 'committee', 'manager'];

    const historySection = menteeApps.length > 0 && (
      <div className="pledge-history">
        <div className="pledge-history-title">
          <span>Triad Pledge History</span>
        </div>
        <div className="pledge-history-list">
          {historyGroupOrder.map(appId => {
            const pledgeGroup = pledgesByApp[appId] || [];
            const menteeApp = menteeApps.find(a => a.id === appId);
            const myUnsigned = !approvedMentees.includes(appId);
            const myRole = menteeApp ? this.determineUserRole(menteeApp) : null;
            const myUserId = this.props.currentUser.user_id;

            // Build a role-ordered list of all tiles: signed entries + pending slots for every unsigned role
            // Deduplicate by role — keep the most-recent pledge per role (guard against dirty data / double-signs)
            const pledgeByRole = {};
            for (const pledge of pledgeGroup) {
              const existing = pledgeByRole[pledge.signer_role];
              if (!existing || new Date(pledge.created_at) > new Date(existing.created_at)) {
                pledgeByRole[pledge.signer_role] = pledge;
              }
            }
            const signedEntries = Object.values(pledgeByRole).map(pledge => ({ type: 'signed', pledge }));
            const signedRoleSet = new Set(Object.keys(pledgeByRole));
            // My pending slot — shows current user's avatar + click-to-expand
            const myPendingEntry = myUnsigned && myRole ? [{ type: 'pending', role: myRole }] : [];
            // Generic pending slots for other unsigned roles (not the current user's)
            const otherPendingEntries = ALL_EXPECTED_ROLES
              .filter(r => !signedRoleSet.has(r) && r !== myRole)
              .map(r => ({ type: 'other-pending', role: r }));
            const allEntries = [...signedEntries, ...myPendingEntry, ...otherPendingEntries].sort((a, b) => {
              const getRole = e => e.type === 'signed' ? e.pledge.signer_role : e.role;
              return (ROLE_ORDER[getRole(a)] ?? 99) - (ROLE_ORDER[getRole(b)] ?? 99);
            });

            // Completion status: all 4 standard roles have a signed pledge
            const signedRoles = signedRoleSet;
            const isGroupComplete = ALL_EXPECTED_ROLES.every(r => signedRoles.has(r));
            const completionBadge = (
              <span className={`grad-pledges-chip ${isGroupComplete ? 'complete' : 'incomplete'}`}>
                {isGroupComplete ? '✓ Complete' : 'Incomplete'}
              </span>
            );

            return (
              <div key={appId} className="pledge-history-group">
                {menteeApp && (
                  <div className="pledge-history-group-header">
                    <UserAvatar user={menteeApp.User} size={24} />
                    <span className="pledge-history-group-label">Triad:</span>
                    <span className="pledge-history-group-name">{stripNameTitle(menteeApp.User?.name)}</span>
                    {menteeApp.Chapter?.location && (
                      <span className="pledge-history-group-chapter">{menteeApp.Chapter.location}</span>
                    )}
                    {completionBadge}
                  </div>
                )}
                {allEntries.map((entry, idx) => {
                  if (entry.type === 'pending') {
                    const pendingKey = `${appId}-${myUserId}`;
                    const isOpen = expandedTileKey === pendingKey;
                    return (
                      <React.Fragment key={`pending-${appId}`}>
                        <div
                          className="pledge-history-item pledge-history-item--pending pledge-history-item--clickable"
                          onClick={() => this.toggleTileTimeline(appId, myUserId)}
                          title="Click to view your history"
                        >
                          <UserAvatar user={this.props.currentUser.User} size={36} />
                          <div className="pledge-history-info">
                            <span className="pledge-history-name pledge-history-name--pending">
                              {stripNameTitle(this.props.currentUser.User?.name || 'You')}
                            </span>
                            <span className="pledge-history-role">{formatRole(entry.role)}</span>
                          </div>
                          <span className="pledge-history-date pledge-history-pending-label">Awaiting signature</span>
                          <FontAwesomeIcon icon={faClock} className="pledge-history-pending-icon" />
                          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="pledge-history-expand-icon" />
                        </div>
                        {isOpen && (
                          <div className="pledge-history-timeline-panel">
                            {renderTimeline(pendingKey)}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  }
                  // Generic unsigned slot — another role that hasn't signed (not the current user)
                  if (entry.type === 'other-pending') {
                    // Resolve the assigned person for this role from application data
                    const assignedUser = (() => {
                      if (entry.role === 'mentor') return menteeApp?.assignedMentor || null;
                      if (entry.role === 'committee') return menteeApp?.assignedCommittee || null;
                      if (entry.role === 'manager') {
                        const email = menteeApp?.manager;
                        if (email) {
                          // Extract NTID from email (e.g. "swathi_inuganti@comcast.com" → "Swathi Inuganti")
                          const ntid = email.split('@')[0];
                          const name = ntid.replace(/[_\.]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                          return { name };
                        }
                        return null;
                      }
                      return null;
                    })();
                    return (
                      <div key={`other-pending-${appId}-${entry.role}`} className="pledge-history-item pledge-history-item--pending">
                        <UserAvatar user={assignedUser} size={36} />
                        <div className="pledge-history-info">
                          <span className="pledge-history-name pledge-history-name--pending">
                            {assignedUser ? stripNameTitle(assignedUser.name) : 'Unassigned'}
                          </span>
                          <span className="pledge-history-role">{formatRole(entry.role)}</span>
                        </div>
                        <span className="pledge-history-date pledge-history-pending-label">Awaiting signature</span>
                        <FontAwesomeIcon icon={faClock} className="pledge-history-pending-icon" />
                      </div>
                    );
                  }
                  const { pledge } = entry;
                  const tileKey = `${appId}-${pledge.signed_by}`;
                  const isOpen = expandedTileKey === tileKey;
                  return (
                    <React.Fragment key={pledge.id}>
                      <div
                        className="pledge-history-item pledge-history-item--clickable"
                        onClick={() => this.toggleTileTimeline(appId, pledge.signed_by)}
                        title="Click to view pledge history"
                      >
                        <UserAvatar user={pledge.User} size={36} />
                        <div className="pledge-history-info">
                          <span className="pledge-history-name">{stripNameTitle(pledge.User?.name || 'Unknown')}</span>
                          <span className="pledge-history-role">{formatRole(pledge.signer_role)}</span>
                        </div>
                        <span className="pledge-history-date">
                          {new Date(pledge.created_at).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            timeZoneName: 'short',
                          })}
                        </span>
                        <FontAwesomeIcon icon={faCheck} className="pledge-history-check" />
                        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="pledge-history-expand-icon" />
                      </div>
                      {isOpen && (
                        <div className="pledge-history-timeline-panel">
                          {renderTimeline(tileKey)}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );

    return (
      <>
        <PrismDialog
          open={showWithdrawModal}
          close-method="user"
          setAriaLabel="Withdraw Pledge"
          onPrismClose={() => this.setState({ showWithdrawModal: false })}
        >
          <h3>Withdraw Pledge</h3>
          {menteeApps.filter(app => approvedMentees.includes(app.id)).length <= 1 ? (
              <p>Are you sure you want to withdraw your pledge? You will need to go through all the commitments again to re-submit.</p>
            ) : (
              <>
                <p>Select which mentee(s) to withdraw your support for. You will need to re-sign for any you withdraw.</p>
                <div className="pledge-withdraw-selector">
                  {menteeApps.filter(app => approvedMentees.includes(app.id)).map(app => {
                    const isSelected = withdrawSelectedIds.has(app.id);
                    return (
                      <div
                        key={app.id}
                        className={`pledge-mentee-tile${isSelected ? ' selected' : ' unselected'}`}
                        onClick={() => this.setState(prev => {
                          const next = new Set(prev.withdrawSelectedIds);
                          if (next.has(app.id)) next.delete(app.id);
                          else next.add(app.id);
                          return { withdrawSelectedIds: next };
                        })}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            this.setState(prev => {
                              const next = new Set(prev.withdrawSelectedIds);
                              if (next.has(app.id)) next.delete(app.id);
                              else next.add(app.id);
                              return { withdrawSelectedIds: next };
                            });
                          }
                        }}
                      >
                        <UserAvatar user={app.User} size={36} />
                        <div className="pledge-mentee-tile-info">
                          <span className="pledge-mentee-tile-name">{stripNameTitle(app.User?.name)}</span>
                          <span className="pledge-mentee-tile-status" style={{ color: isSelected ? 'var(--color-danger-dk)' : 'var(--color-success-dk)' }}>
                            {isSelected ? '✕ Will withdraw' : '✓ Keep'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          <PrismButton
            slot="footer"
            tone="secondary"
            display="outline"
            onPrismClick={() => this.setState({ showWithdrawModal: false })}
          >
            Cancel
          </PrismButton>
          <PrismButton
            slot="footer"
            tone="danger"
            onPrismClick={this.confirmRevoke.bind(this)}
            disabled={withdrawSelectedIds.size === 0}
          >
            {withdrawSelectedIds.size <= 1 ? 'Yes, Withdraw' : `Withdraw for ${withdrawSelectedIds.size} Mentees`}
          </PrismButton>
        </PrismDialog>

        <PageHeader title="Graduation Pledge" />

        <div className="pledge-page">
          {noApplications ? (
            <div className="pledge-card">
              <div className="pledge-card-header">
                <h2><span className="pledge-card-icon">📋</span> Program Commitment <span className="pledge-card-icon">📋</span></h2>
              </div>
              <div className="pledge-card-body">
                <PrismBanner tone="info" layout="inline">
                  No accepted applications found for this year. Please contact your program coordinator.
                </PrismBanner>
              </div>
            </div>
          ) : approvedAll ? (
            <>
              <div className="pledge-card">
                <div className="pledge-card-header pledge-card-header--complete">
                  <h2><span className="pledge-card-icon">🎓</span> Pledge Complete <span className="pledge-card-icon">🎓</span></h2>
                </div>
                <div className="pledge-card-body pledge-complete">
                  {!isMentee && menteeApps.length > 0 && (
                    <div className="pledge-mentee-selector">
                      <p className="pledge-mentee-selector-label">Signed for:</p>
                      <div className="pledge-mentee-tiles">
                        {menteeApps.map((app) => (
                          <div key={app.id} className="pledge-mentee-tile signed non-interactive">
                            <UserAvatar user={app.User} size={40} />
                            <div className="pledge-mentee-tile-info">
                              <span className="pledge-mentee-tile-name">{stripNameTitle(app.User?.name)}</span>
                              <span className="pledge-mentee-tile-status signed">✓ Signed</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="pledge-complete-check">✓</div>
                  <p className="pledge-complete-msg">
                    You have completed your pledge(s) for this year. Thank you!
                  </p>
                  {signedDate && (
                    <p className="pledge-complete-date">
                      Signed on <strong>{signedDate}</strong>
                    </p>
                  )}
                  {isMentee && (
                    <p className="pledge-complete-mentee">Your pledge has been recorded.</p>
                  )}
                  <div className="pledge-withdraw">
                    <PrismButton
                      tone="danger"
                      display="outline"
                      size="sm"
                      onPrismClick={this.handleRevoke.bind(this)}
                    >
                      <FontAwesomeIcon icon={faUndoAlt} /> Withdraw Pledge
                    </PrismButton>
                    <p className="pledge-withdraw-note">
                      Changed your mind? Withdrawing will remove your pledge and require you to re-submit.
                    </p>
                  </div>
                </div>
              </div>
              {historySection}
            </>
          ) : (
            <>
              <div className="pledge-card">
                <div className="pledge-card-header">
                  <h2><span className="pledge-card-icon">📋</span> Program Commitment <span className="pledge-card-icon">📋</span></h2>

                </div>

                <div className="pledge-card-body">
                  {!isMentee && menteeApps.length > 0 && (
                    <div className="pledge-mentee-selector">
                      <p className="pledge-mentee-selector-label">
                        {menteeApps.length === 1 ? 'Signing for:' : 'Select who to sign for:'}
                      </p>
                      <div className="pledge-mentee-tiles">
                        {menteeApps.map(app => {
                          const alreadySigned = approvedMentees.includes(app.id);
                          const isSelected = selectedAppIds.has(app.id);
                          const canToggle = !alreadySigned && menteeApps.length > 1;
                          return (
                            <div
                              key={app.id}
                              className={`pledge-mentee-tile${alreadySigned ? ' signed' : isSelected ? ' selected' : ' unselected'}${canToggle ? '' : ' non-interactive'}`}
                              onClick={() => {
                                if (!canToggle) return;
                                this.setState(prev => {
                                  const next = new Set(prev.selectedAppIds);
                                  if (next.has(app.id)) next.delete(app.id);
                                  else next.add(app.id);
                                  return { selectedAppIds: next };
                                });
                              }}
                              role={canToggle ? 'checkbox' : undefined}
                              aria-checked={canToggle ? isSelected : undefined}
                              tabIndex={canToggle ? 0 : undefined}
                              onKeyDown={canToggle ? (e => {
                                if (e.key === ' ' || e.key === 'Enter') {
                                  e.preventDefault();
                                  this.setState(prev => {
                                    const next = new Set(prev.selectedAppIds);
                                    if (next.has(app.id)) next.delete(app.id);
                                    else next.add(app.id);
                                    return { selectedAppIds: next };
                                  });
                                }
                              }) : undefined}
                            >
                              <UserAvatar user={app.User} size={40} />
                              <div className="pledge-mentee-tile-info">
                                <span className="pledge-mentee-tile-name">{stripNameTitle(app.User?.name)}</span>
                                {alreadySigned
                                  ? <span className="pledge-mentee-tile-status signed">✓ Already signed</span>
                                  : isSelected
                                    ? <span className="pledge-mentee-tile-status selected">✓ Will sign</span>
                                    : <span className="pledge-mentee-tile-status unselected">Click to include</span>
                                }
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <p className="pledge-intro">
                    Please read each commitment carefully and check each box to acknowledge it before proceeding to the next.
                  </p>

                  <div className="pledge-progress-bar">
                    <div
                      className="pledge-progress-fill"
                      style={{ width: `${Math.round((checkedItems.size / commitments.length) * 100)}%` }}
                    />
                  </div>
                  <p className="pledge-progress-label">
                    {checkedItems.size} of {commitments.length} commitments acknowledged
                  </p>

                  <ol className="pledge-commitments">
                    {commitments.map((c, i) => {
                      const isLocked = i > 0 && !checkedItems.has(i - 1);
                      const isChecked = checkedItems.has(i);
                      return (
                        <li
                          key={i}
                          className={`pledge-commitment-item${isChecked ? ' checked' : ''}${isLocked ? ' locked' : ''}`}
                        >
                          <label className="pledge-commitment-label">
                            <input
                              type="checkbox"
                              className="pledge-commitment-checkbox"
                              checked={isChecked}
                              disabled={isLocked}
                              onChange={() => this.toggleCommitment(i)}
                            />
                            <span className="pledge-commitment-text">{c}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ol>

                  <div className="pledge-actions">
                    {isMentee ? (
                      <p className="pledge-submit-note">
                        As a mentee in the CLEAR program, I acknowledge these commitments. My network is your network.
                      </p>
                    ) : (
                      <p className="pledge-submit-note">
                        I acknowledge that in addition to supporting and upholding the above values, I promise to commit myself to supporting this mentee's success. My network is your network.
                      </p>
                    )}
                    <PrismButton
                      onPrismClick={this.handleSubmit.bind(this)}
                      disabled={!canSubmit}
                      tone={canSubmit ? 'success' : 'secondary'}
                      size="lg"
                    >
                      <FontAwesomeIcon icon={faCheck} /> {submitLabel}
                    </PrismButton>
                  </div>
                </div>
              </div>
              {historySection}
            </>
          )}
        </div>
      </>
    );
  };
}

export default Pledge;
